import type { FC, ReactNode } from 'react';
import { Button, Column, ContentSwitcher, Grid, Layer, ListItem, Stack, StructuredListBody, StructuredListCell, StructuredListRow, StructuredListWrapper, Switch, Tag, Tile, UnorderedList } from '@carbon/react';
import { Aperture, ArrowRight, Badge, Grid as IconeGrade } from '@carbon/icons-react';

import type { ChaveDoBeneficio, GrupoDeBeneficios } from '@maestra/core/constants/planos';
import type { DadosDosPlanos } from '../../../logica/dinheiro/usePlanos';
import type { DadosDoPainelDaAssinatura } from '../../../logica/dinheiro/usePainelDaAssinatura';
import { IconeDaNyta } from '../../marcas/MarcaDaNyta';
import { CarregandoPagina } from '../../padroes/Carregando';
import { ConfirmarPerigo } from '../../padroes/ConfirmarPerigo';
import { PaginaDeConta } from '../../padroes/PaginaDeConta';
import { UsoDoEspaco } from '../../padroes/UsoDoEspaco';
import styles from './Dinheiro.module.scss';

// A /PLANOS no Carbon, conforme quem olha:
//  - quem não assina: a comparação Grátis × PRO (dois Tiles lado a lado, o ciclo no alto) e, depois
//    de "Ir para pagamento", o checkout;
//  - quem tem a assinatura pendente: o que escolheu, numa lista, e as duas saídas (pagar o que já
//    foi gerado, ou alterar o plano e a forma de pagamento, com confirmação). Sem "Ver assinatura":
//    as Configurações não tinham nada a dizer ali (pedido do Lucas, 04/10/2026);
//  - quem assina (ou está em atraso): o painel da assinatura.

const ICONE: Record<ChaveDoBeneficio, ReactNode> = {
  executar: <Aperture size={20} aria-hidden />,
  nyta: <IconeDaNyta size={20} />,
  gestao: <IconeGrade size={20} aria-hidden />,
  acompanhar: <Badge size={20} aria-hidden />,
};

const Beneficios: FC<{ grupos: readonly GrupoDeBeneficios[] }> = ({ grupos }) => (
  <Stack gap={6}>
    {grupos.map((g) => (
      <div key={g.chave}>
        <p className={styles.grupo}>{ICONE[g.chave]}<span>{g.titulo}</span></p>
        <UnorderedList className={styles.itens}>{g.itens.map((i) => <ListItem key={i}>{i}</ListItem>)}</UnorderedList>
      </div>
    ))}
  </Stack>
);

const Vitrine: FC<{ v: DadosDosPlanos['vitrine'] }> = ({ v }) => (
  <PaginaDeConta titulo={v.titulo} descricao={v.apoio} larga>
    <Stack gap={6}>
      {v.ciclo && (
        <div className={styles.ciclo}>
          <ContentSwitcher size='md' selectedIndex={v.ciclo.valor === 'YEARLY' ? 1 : 0}
            onChange={({ index }) => v.ciclo?.mudar(index === 1 ? 'YEARLY' : 'MONTHLY')}>
            <Switch name='MONTHLY' text='Mensal' />
            <Switch name='YEARLY' text={v.ciclo.economia ? `Anual · ${v.ciclo.economia}` : 'Anual'} />
          </ContentSwitcher>
        </div>
      )}
      <Grid narrow className={styles.grade}>
        <Column sm={4} md={4} lg={8}>
          <Tile className={styles.plano}>
            <Stack gap={6}>
              <div>
                <h2 className={styles.nomeDoPlano}>{v.gratis.nome}</h2>
                <p className={styles.apoio}>{v.gratis.descricao}</p>
              </div>
              <p className={styles.preco}>{v.gratis.preco}</p>
              <Beneficios grupos={v.gratis.grupos} />
            </Stack>
          </Tile>
        </Column>
        <Column sm={4} md={4} lg={8}>
          <Tile className={`${styles.plano} ${styles.planoPro}`}>
            <Stack gap={6}>
              <div>
                <h2 className={styles.nomeDoPlano}>{v.pro.nome}</h2>
                <p className={styles.apoio}>{v.pro.descricao}</p>
              </div>
              <div>
                <p className={styles.preco}>{v.pro.preco}<span className={styles.unidade}>{v.pro.unidade}</span></p>
                {v.pro.nota && <p className={styles.apoio}>{v.pro.nota}</p>}
              </div>
              <Button kind='primary' size='lg' renderIcon={ArrowRight} onClick={v.irParaPagamento} className={styles.botaoCheio}>Ir para pagamento</Button>
              <Beneficios grupos={v.pro.grupos} />
            </Stack>
          </Tile>
        </Column>
      </Grid>
    </Stack>
  </PaginaDeConta>
);

/** O painel de quem assina: o plano, o que se usa, a cobrança e o que se pode fazer. */
export const PainelDaAssinatura: FC<{ p: DadosDoPainelDaAssinatura }> = ({ p }) => (
  <PaginaDeConta titulo='Seu plano' descricao='O que você paga, o que tem e quanto disso está usando.' larga
    acoes={(
      <>
        <Button kind='tertiary' size='md' onClick={p.historico}>Histórico de pagamentos</Button>
        {p.principal && <Button kind='primary' size='md' renderIcon={ArrowRight} onClick={p.principal.fazer}>{p.principal.rotulo}</Button>}
      </>
    )}>
    <Stack gap={6}>
      <Tile className={styles.bloco}>
        <div className={styles.cabecaDoPlano}>
          <div>
            <p className={styles.rotuloPequeno}>Sua assinatura</p>
            <h2 className={styles.nomeDoPlano}>Maestra PRO</h2>
            {p.alerta && <Tag type={p.tomDoAlerta} size='md'>{p.alerta}</Tag>}
          </div>
          <div className={styles.precoAlinhado}>
            <p className={styles.preco}>{p.preco}<span className={styles.unidade}>{p.unidade}</span></p>
            <p className={styles.apoio}>{p.notaDoPreco}</p>
          </div>
        </div>
      </Tile>
      <Grid narrow className={styles.grade}>
        <Column sm={4} md={4} lg={8}>
          <Tile className={styles.bloco}>
            <Stack gap={4}>
              <h3 className={styles.rotuloPequeno}>Perfis de artista</h3>
              <p className={styles.numero}>{p.perfis.usados}<span className={styles.unidade}>{` de ${p.perfis.contratados}`}</span></p>
              <p className={styles.apoio}>{p.perfis.detalhe}</p>
              {p.perfis.agendado && <Tag type='blue' size='md'>{p.perfis.agendado}</Tag>}
            </Stack>
          </Tile>
        </Column>
        <Column sm={4} md={4} lg={8}>
          <Tile className={styles.bloco}>
            <Stack gap={4}>
              <h3 className={styles.rotuloPequeno}>Armazenamento</h3>
              <p className={styles.numero}>{p.espaco.usado}<span className={styles.unidade}>{` de ${p.espaco.contratado}`}</span></p>
              <UsoDoEspaco rotulo='Espaço usado' percentual={p.espaco.percentual} cheio={p.espaco.cheio} texto={p.espaco.detalhe} />
              {p.espaco.agendado && <Tag type='blue' size='md'>{p.espaco.agendado}</Tag>}
            </Stack>
          </Tile>
        </Column>
      </Grid>
      <Tile className={styles.bloco}>
        <Layer>
          <h3 className={styles.rotuloPequeno}>Cobrança</h3>
          <StructuredListWrapper isCondensed aria-label='Cobrança' className={styles.lista}>
            <StructuredListBody>
              {p.cobranca.map((l) => (
                <StructuredListRow key={l.rotulo}>
                  <StructuredListCell>{l.rotulo}</StructuredListCell>
                  <StructuredListCell className={`${styles.valor} ${l.aviso ? styles.emAviso : ''}`}>{l.valor}</StructuredListCell>
                </StructuredListRow>
              ))}
            </StructuredListBody>
          </StructuredListWrapper>
        </Layer>
      </Tile>
      {/* A saída existe e está à mão, mas não disputa atenção: é o botão de perigo discreto. */}
      {p.cancelamento.disponivel && (
        <div><Button kind='danger--ghost' size='md' disabled={p.cancelamento.emCurso} onClick={p.cancelamento.pedir}>Cancelar assinatura</Button></div>
      )}
    </Stack>
    <ConfirmarPerigo
      aberto={p.cancelamento.aberto}
      rotulo='Assinatura'
      titulo='Cancelar a assinatura?'
      texto='Ao confirmar, as cobranças param. O PRO segue até o fim do período já pago, e nada do que você criou é apagado.'
      acao='Sim, cancelar'
      manter='Manter a assinatura'
      emCurso={p.cancelamento.emCurso}
      aoConfirmar={p.cancelamento.confirmar}
      aoFechar={p.cancelamento.fechar}
    />
  </PaginaDeConta>
);

export const Planos: FC<{ p: DadosDosPlanos; painel: ReactNode; checkout: ReactNode }> = ({ p, painel, checkout }) => {
  if (p.estado === 'carregando') return <CarregandoPagina />;
  if (p.estado === 'painel') return <>{painel}</>;
  if (p.estado === 'pendente') {
    const x = p.pendente;
    return (
      <PaginaDeConta titulo={x.titulo} descricao={x.texto}>
        <Stack gap={6} className={styles.encolhe}>
          <Tile className={styles.bloco}>
            <Layer>
              <h2 className={styles.rotuloPequeno}>O que você escolheu</h2>
              <StructuredListWrapper isCondensed aria-label='O que você escolheu' className={styles.lista}>
                <StructuredListBody>
                  {x.resumo.map((l) => (
                    <StructuredListRow key={l.rotulo}>
                      <StructuredListCell>{l.rotulo}</StructuredListCell>
                      <StructuredListCell className={styles.valor}>{l.valor}</StructuredListCell>
                    </StructuredListRow>
                  ))}
                </StructuredListBody>
              </StructuredListWrapper>
            </Layer>
          </Tile>
          <div className={styles.acoes}>
            <Button kind='primary' size='md' renderIcon={ArrowRight} onClick={x.retomar} disabled={x.alterar.emCurso}>{x.pagar}</Button>
            <Button kind='ghost' size='md' onClick={x.alterar.pedir} disabled={x.alterar.emCurso}>Alterar plano ou pagamento</Button>
          </div>
        </Stack>
        <ConfirmarPerigo
          aberto={x.alterar.aberto}
          perigo={false}
          rotulo='Assinatura'
          titulo='Alterar plano ou pagamento?'
          texto={x.alterar.texto}
          acao='Alterar'
          manter='Manter como está'
          emCurso={x.alterar.emCurso}
          aoConfirmar={x.alterar.alterar}
          aoFechar={x.alterar.fechar}
        />
      </PaginaDeConta>
    );
  }
  if (p.estado === 'checkout') {
    return (
      <PaginaDeConta titulo='Pagamento' descricao='Confira o plano e escolha como pagar.' larga
        trilha={[{ rotulo: 'Seus perfis', caminho: '/artists' }, { rotulo: 'Planos', caminho: '/planos' }]}
        acoes={<Button kind='ghost' size='md' onClick={p.checkout.voltar}>Voltar aos planos</Button>}>
        {checkout}
      </PaginaDeConta>
    );
  }
  return <Vitrine v={p.vitrine} />;
};
